import * as React from 'react';
import classnames from 'classnames';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { isSameYear, parseISO } from 'date-fns';

import { CdnIcon } from '@shared/ui/CdnIcon';
import { MonthMoney, getProgressLevel } from '@features/price-graph';
import { Money } from '../../../../../../Money/components/Money';
import { useTheme } from '../../../../../../theme';
import DropdownAdapter from '../DropdownAdapter/DropdownAdapter';
import { MOBILE_MIN_WIDTH, TABLET_MIN_WIDTH, format } from '../../../../../../utils';

interface Props {
	months: MonthMoney[];
	selectedMonth: string;
	onChange: (dateISO: string) => void;
}

const MonthsList: React.FC<Props> = ({ months, selectedMonth, onChange }) => {
	const { t } = useTranslation('PriceGraph');
	const theme = useTheme('Features').PriceGraph.MonthsListStyles;

	const [isOpen, setVisibility] = React.useState(false),
		currentMonth = new Date(selectedMonth);

	const isOnChange = (dateISO: string) => {
		setVisibility(false);

		onChange(dateISO);
	};
	const minPrice = {
		amount: months[0].minPrice ? months[0].minPrice.amount : 0,
		currency: months[0].minPrice ? months[0].minPrice.currency : null
	};
	const maxPrice = {
		amount: 0,
		currency: months[0].minPrice ? months[0].minPrice.currency : null
	};

	const firstMonthYear = months[0].dateISO;

	months.map(month => {
		if (month.minPrice) {
			if (month.minPrice.amount < minPrice.amount) {
				minPrice.amount = month.minPrice.amount;
			}

			if (month.minPrice.amount > maxPrice.amount) {
				maxPrice.amount = month.minPrice.amount;
			}
		}
	});

	return (
		<>
			<div className={theme.header}>
				<div className={theme.currentMonth} onClick={() => setVisibility(!isOpen)}>
					<span className={theme.currentMonth__monthName}>{format(currentMonth, 'LLLL')}</span>
					<span className={theme.currentMonth__year}>{currentMonth.getFullYear()}</span>
					<CdnIcon
						icon={'Arrow/Drop_up'}
						className={classnames(theme.arrow_icon, { [theme.arrow_icon__open]: isOpen })}
					/>
				</div>
				<div className={theme.info_details}>
					<CdnIcon icon={'Info/Details'} />
					<MediaQuery minWidth={MOBILE_MIN_WIDTH}>{t('Price is for all passengers, one way')}</MediaQuery>
					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>{t(`Estimated prices for one person, one way`)}</MediaQuery>
				</div>
			</div>

			<DropdownAdapter isOpen={isOpen} onClose={() => setVisibility(false)}>
				<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
					<div className={theme.mobileHeader}>{t('Select month')}</div>
				</MediaQuery>

				<div className={theme.monthList}>
					{months.map((month, index) => {
						return (
							<div
								key={index}
								className={classnames(theme.month, {
									[theme.month_min]: month.minPrice && month.minPrice.amount === minPrice.amount,
									[theme.month_max]: month.minPrice && month.minPrice.amount === maxPrice.amount,
									[theme.month__price__month_min]:
										month.minPrice && month.minPrice.amount === minPrice.amount,
									[theme.month__price__month_max]:
										month.minPrice && month.minPrice.amount !== minPrice.amount
								})}
								onClick={() => isOnChange(month.dateISO)}
							>
								<div className={theme.month__name}>
									{format(
										new Date(month.dateISO),
										!isSameYear(parseISO(firstMonthYear), parseISO(month.dateISO))
											? 'LLLL yyyy'
											: 'LLLL'
									)}
								</div>

								{month.minPrice && (
									<Money
										moneyClassName={theme.month__price}
										money={month.minPrice}
										showCurrencyBeforeAmount={true}
									/>
								)}

								<div className={theme.bar}>
									<div
										className={theme.progress}
										style={{
											width: `${getProgressLevel(
												month.minPrice ? month.minPrice.amount : 0,
												maxPrice.amount
											)}%`
										}}
									/>
								</div>
							</div>
						);
					})}
				</div>
			</DropdownAdapter>
		</>
	);
};
export default MonthsList;
